import { useState, useEffect } from "react";
import { 
  Droplets, 
  FlaskConical, 
  TestTube, 
  Microscope,
  ShoppingCart,
  Plus,
  Minus,
  Trash2,
  CheckCircle2,
  Clock,
  Building2,
  FileText,
  Send,
  AlertCircle,
  Loader2,
  Award,
  Phone,
  Mail,
  MapPin,
  ExternalLink,
  Package,
  DollarSign,
  Beaker,
  Shield,
  Zap,
  Timer,
  ChevronRight,
  BadgeCheck,
  Sparkles,
  Activity,
  Play,
  Calendar,
  Repeat,
  Upload,
  X,
  Check,
  CalendarDays,
  ClipboardList,
  Download,
  Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || "";

// AB Labs Logo
const AB_LABS_LOGO = "https://ablabs.com/wp-content/uploads/2022/01/logo.png";
const STERILEMATE_LOGO = "/logos/sterilemate-white.png?v=4";

// Category icons mapping
const categoryIcons = {
  chemical: FlaskConical,
  microbiological: Microscope,
  legionella: Activity,
  drinking_water: Droplets,
  environmental: TestTube
};

const categoryColors = {
  chemical: "bg-blue-100 text-blue-700 border-blue-200",
  microbiological: "bg-purple-100 text-purple-700 border-purple-200",
  legionella: "bg-red-100 text-red-700 border-red-200",
  drinking_water: "bg-cyan-100 text-cyan-700 border-cyan-200",
  environmental: "bg-emerald-100 text-emerald-700 border-emerald-200"
};

// Microbiology test categories
const MICROBIOLOGY_CATEGORIES = [
  {
    id: "legionella",
    title: "Legionella Testing",
    subtitle: "Same-Day Results Available",
    icon: Activity,
    color: "from-red-500 to-rose-600",
    featured: true,
    tests: [
      { name: "Legionella PCR (Same-Day)", description: "In by 9am, results by 5pm", turnaround: "Same Day", price: 150 },
      { name: "Legionella Pneumophila Serogroup 1", description: "CDC ELITE certified method", turnaround: "3-5 days", price: 95 },
      { name: "Legionella Pneumophila Serogroup 2-14", description: "Full serogroup identification", turnaround: "3-5 days", price: 95 },
      { name: "Legionella Species", description: "Complete Legionella identification", turnaround: "5-7 days", price: 85 }
    ]
  },
  {
    id: "drinking_water",
    title: "Drinking Water Microbiology",
    subtitle: "EPA & TCEQ Compliant",
    icon: Droplets,
    color: "from-cyan-500 to-blue-600",
    tests: [
      { name: "Total Coliforms & E.coli", description: "Enzyme substrate method", turnaround: "24-48 hrs", price: 35 },
      { name: "Enterococcus", description: "Membrane filtration method", turnaround: "24-48 hrs", price: 40 },
      { name: "Fecal Coliform", description: "Membrane filtration method", turnaround: "24-48 hrs", price: 35 },
      { name: "Heterotrophic Plate Count", description: "Pour plate method", turnaround: "48-72 hrs", price: 30 }
    ]
  },
  {
    id: "environmental",
    title: "Environmental Microbiology",
    subtitle: "Air, Surface & Bulk Samples",
    icon: Microscope,
    color: "from-purple-500 to-violet-600",
    tests: [
      { name: "Yeast & Mold Identification", description: "Culturable analysis", turnaround: "5-7 days", price: 45 },
      { name: "Bacterial Identification", description: "Genus level identification", turnaround: "3-5 days", price: 55 },
      { name: "Air Sample Analysis", description: "Culturable & non-culturable", turnaround: "3-5 days", price: 35 },
      { name: "MSQPCR Mold Testing", description: "Quantitative PCR analysis", turnaround: "3-5 days", price: 125 }
    ]
  },
  {
    id: "industrial",
    title: "Industrial Water Testing",
    subtitle: "Cooling Towers & Process Water",
    icon: Building2,
    color: "from-amber-500 to-orange-600",
    tests: [
      { name: "Pseudomonas Aeruginosa", description: "Industrial water pathogen", turnaround: "48-72 hrs", price: 45 },
      { name: "Iron & Sulfate Reducing Bacteria", description: "Corrosion causing organisms", turnaround: "7-10 days", price: 65 },
      { name: "Microbiologically Influenced Corrosion (MIC)", description: "Full MIC panel", turnaround: "7-10 days", price: 150 },
      { name: "Slime-Forming Bacteria", description: "Biofilm assessment", turnaround: "5-7 days", price: 55 }
    ]
  }
];

// Accreditations
const ACCREDITATIONS = [
  { name: "ISO/IEC 17025:2017", description: "International quality standard" },
  { name: "NELAP Accredited", description: "National Environmental Laboratory" },
  { name: "TCEQ Approved", description: "Texas Commission on Env. Quality" },
  { name: "CDC ELITE", description: "Legionella certification" },
  { name: "AIHA LAP", description: "Industrial Hygiene accreditation" },
  { name: "NVLAP", description: "Asbestos testing certification" }
];

export default function EnvironmentalTestingPage({ healthSystemId, userInfo }) {
  const [catalog, setCatalog] = useState({ tests: [], fees: [], minimum_invoice: 0 });
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [samplePoints, setSamplePoints] = useState([""]);
  const [locationName, setLocationName] = useState("");
  const [notes, setNotes] = useState("");
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState("catalog");
  
  // Order detail/edit state
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetailDialog, setShowOrderDetailDialog] = useState(false);
  
  // Enhanced checkout state
  const [poNumber, setPoNumber] = useState("");
  const [poFile, setPoFile] = useState(null);
  const [poFileName, setPoFileName] = useState("");
  const [uploadingPo, setUploadingPo] = useState(false);
  
  // Recurring schedule state
  const [showScheduleBuilder, setShowScheduleBuilder] = useState(false);
  const [scheduleFrequency, setScheduleFrequency] = useState("monthly");
  const [scheduleStartDate, setScheduleStartDate] = useState("");
  const [scheduledSamplePoints, setScheduledSamplePoints] = useState([
    { name: "", location: "", description: "" }
  ]);
  const [selectedScheduleTests, setSelectedScheduleTests] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [savingSchedule, setSavingSchedule] = useState(false);
  
  // Contact dialog state
  const [showContactDialog, setShowContactDialog] = useState(false);

  useEffect(() => {
    fetchCatalog();
    fetchOrders();
  }, []);

  const fetchCatalog = async () => {
    try {
      const res = await fetch(`${API}/api/environmental-testing/catalog`);
      const data = await res.json();
      setCatalog(data);
    } catch (err) {
      toast.error("Failed to load testing catalog");
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      const res = await fetch(`${API}/api/environmental-testing/orders?health_system_id=${healthSystemId}`);
      const data = await res.json();
      setOrders(data.orders || []);
    } catch (err) {
      console.error("Failed to fetch orders:", err);
    }
  };

  const addToCart = (test) => {
    const existing = cart.find(item => item.id === test.id);
    if (existing) {
      toast.info(`${test.name} is already in your cart`);
      return;
    }
    setCart([...cart, { ...test, quantity: 1 }]);
    toast.success(`Added ${test.name} to cart`);
  };

  const removeFromCart = (testId) => {
    setCart(cart.filter(item => item.id !== testId));
  };

  const addSamplePoint = () => {
    setSamplePoints([...samplePoints, ""]);
  };

  const removeSamplePoint = (index) => {
    if (samplePoints.length > 1) {
      setSamplePoints(samplePoints.filter((_, i) => i !== index));
    }
  };

  const updateSamplePoint = (index, value) => {
    const updated = [...samplePoints];
    updated[index] = value;
    setSamplePoints(updated);
  };

  // PO File Upload Handler
  const handlePoFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error("File size must be less than 10MB");
        return;
      }
      setPoFile(file);
      setPoFileName(file.name);
      toast.success(`PO file "${file.name}" attached`);
    }
  };

  const removePoFile = () => {
    setPoFile(null);
    setPoFileName("");
  };

  // Schedule Management Functions
  const addScheduledSamplePoint = () => {
    setScheduledSamplePoints([...scheduledSamplePoints, { name: "", location: "", description: "" }]);
  };

  const removeScheduledSamplePoint = (index) => {
    if (scheduledSamplePoints.length > 1) {
      setScheduledSamplePoints(scheduledSamplePoints.filter((_, i) => i !== index));
    }
  };

  const updateScheduledSamplePoint = (index, field, value) => {
    const updated = [...scheduledSamplePoints];
    updated[index][field] = value;
    setScheduledSamplePoints(updated);
  };

  const toggleScheduleTest = (test) => {
    const exists = selectedScheduleTests.find(t => t.id === test.id);
    if (exists) {
      setSelectedScheduleTests(selectedScheduleTests.filter(t => t.id !== test.id));
    } else {
      setSelectedScheduleTests([...selectedScheduleTests, test]);
    }
  };

  const saveRecurringSchedule = async () => {
    if (!scheduleStartDate || scheduledSamplePoints.filter(sp => sp.name).length === 0 || selectedScheduleTests.length === 0) {
      toast.error("Please fill in all required schedule fields");
      return;
    }

    setSavingSchedule(true);
    try {
      const scheduleData = {
        health_system_id: healthSystemId,
        frequency: scheduleFrequency,
        start_date: scheduleStartDate,
        sample_points: scheduledSamplePoints.filter(sp => sp.name),
        tests: selectedScheduleTests.map(t => ({ id: t.id, name: t.name, price: t.price })),
        created_by: userInfo?.email,
        status: "active"
      };

      const res = await fetch(`${API}/api/environmental-testing/schedules`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(scheduleData)
      });

      if (res.ok) {
        toast.success("Recurring schedule created successfully!");
        setShowScheduleBuilder(false);
        resetScheduleForm();
        fetchSchedules();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to create schedule");
      }
    } catch (err) {
      toast.error("Failed to save schedule");
    } finally {
      setSavingSchedule(false);
    }
  };

  const resetScheduleForm = () => {
    setScheduleFrequency("monthly");
    setScheduleStartDate("");
    setScheduledSamplePoints([{ name: "", location: "", description: "" }]);
    setSelectedScheduleTests([]);
  };

  const fetchSchedules = async () => {
    try {
      const res = await fetch(`${API}/api/environmental-testing/schedules?health_system_id=${healthSystemId}`);
      const data = await res.json();
      setSchedules(data.schedules || []);
    } catch (err) {
      console.error("Failed to fetch schedules:", err);
    }
  };

  useEffect(() => {
    fetchSchedules();
  }, [healthSystemId]);

  const calculateTotal = () => {
    const sampleCount = samplePoints.filter(sp => sp.trim()).length || 1;
    let subtotal = cart.reduce((sum, item) => sum + (item.price * sampleCount), 0);
    
    // Add fees
    const envFee = catalog.fees.find(f => f.id === "env_fee")?.price || 0;
    const shipping = catalog.fees.find(f => f.id === "shipping")?.price || 0;
    
    let total = subtotal + envFee + shipping;
    
    // Apply minimum
    if (total < catalog.minimum_invoice && cart.length > 0) {
      total = catalog.minimum_invoice;
    }
    
    return { subtotal, envFee, shipping, total, sampleCount };
  };

  const handleSubmitOrder = async () => {
    const validSamplePoints = samplePoints.filter(sp => sp.trim());
    if (validSamplePoints.length === 0) {
      toast.error("Please add at least one sample point");
      return;
    }
    if (cart.length === 0) {
      toast.error("Please select at least one test");
      return;
    }
    if (!locationName.trim()) {
      toast.error("Please enter a location name");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/environmental-testing/orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          health_system_id: healthSystemId,
          location_name: locationName,
          sample_points: validSamplePoints,
          test_ids: cart.map(t => t.id),
          notes: notes,
          requested_by: userInfo?.name || "Unknown",
          requested_by_email: userInfo?.email || ""
        })
      });

      const data = await res.json();
      if (res.ok) {
        toast.success(`Order ${data.order_id} submitted successfully!`);
        setShowOrderDialog(false);
        setCart([]);
        setSamplePoints([""]);
        setLocationName("");
        setNotes("");
        fetchOrders();
        setActiveTab("orders");
      } else {
        toast.error(data.detail || "Failed to submit order");
      }
    } catch (err) {
      toast.error("Failed to submit order");
    } finally {
      setSubmitting(false);
    }
  };

  const { subtotal, envFee, shipping, total, sampleCount } = calculateTotal();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="environmental-testing-page">
      {/* Partnership Hero Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900">
        <div className="absolute inset-0 opacity-5 bg-grid-pattern" />
        <div className="relative px-6 py-8 md:px-10 md:py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            {/* Left Side - Content */}
            <div className="flex-1 text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-3 mb-4">
                <img src={STERILEMATE_LOGO} alt="Sterile Mate" className="h-10 object-contain" />
                <span className="text-cyan-400 text-2xl">×</span>
                <div className="bg-white rounded-lg px-3 py-1">
                  <span className="font-bold text-slate-800">A&B Labs</span>
                </div>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-white mb-3">
                Microbiology & Environmental Testing Partnership
              </h1>
              <p className="text-slate-300 text-lg max-w-xl">
                ISO 17025 accredited laboratory services through our exclusive partnership with A&B Labs, 
                serving healthcare facilities across Texas and the Gulf Coast.
              </p>
              
              {/* Featured Service */}
              <div className="mt-6 inline-flex items-center gap-3 bg-gradient-to-r from-red-500/20 to-rose-500/20 border border-red-500/30 rounded-xl px-5 py-3">
                <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <div className="text-left">
                  <p className="text-red-400 text-xs font-semibold uppercase tracking-wider">Same-Day Service</p>
                  <p className="text-white font-bold">Legionella PCR Testing</p>
                  <p className="text-slate-400 text-sm">In by 9am • Results by 5pm</p>
                </div>
              </div>
            </div>
            
            {/* Right Side - Accreditations */}
            <div className="flex-shrink-0">
              <div className="bg-white/10 backdrop-blur rounded-xl p-5">
                <p className="text-cyan-400 text-xs font-semibold uppercase tracking-wider mb-3 text-center">Accreditations</p>
                <div className="grid grid-cols-3 gap-2">
                  {ACCREDITATIONS.slice(0, 6).map((acc, idx) => (
                    <div key={idx} className="bg-white/10 rounded-lg p-2 text-center">
                      <BadgeCheck className="w-5 h-5 text-cyan-400 mx-auto mb-1" />
                      <p className="text-white text-xs font-medium leading-tight">{acc.name}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-white rounded-xl p-4 shadow-sm border border-slate-200">
        <div className="flex items-center gap-4">
          <a
            href="https://ablabs.com/environmental-coc-071708.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors font-medium"
            data-testid="coc-form-btn"
          >
            <FileText className="w-4 h-4" />
            Chain of Custody Form
            <ExternalLink className="w-3 h-3" />
          </a>
          <a
            href="https://customer-assets.emergentagent.com/job_18b66eae-8401-4add-ae59-ffa20d1894f1/artifacts/yhl0cnx4_new-client-guide.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium"
            data-testid="new-client-guide-btn"
          >
            <FileText className="w-4 h-4" />
            New Client Guide
            <ExternalLink className="w-3 h-3" />
          </a>
          <a
            href="tel:2814563458"
            className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors font-medium"
          >
            <Phone className="w-4 h-4" />
            (281) 456-3458
          </a>
        </div>
        {cart.length > 0 && (
          <Button 
            onClick={() => setShowOrderDialog(true)}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="review-order-btn"
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            Review Order ({cart.length})
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200 overflow-x-auto">
        <button
          onClick={() => setActiveTab("catalog")}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors whitespace-nowrap ${
            activeTab === "catalog" 
              ? "border-cyan-500 text-cyan-600" 
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          Test Catalog
        </button>
        <button
          onClick={() => setActiveTab("microbiology")}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors whitespace-nowrap flex items-center gap-2 ${
            activeTab === "microbiology" 
              ? "border-cyan-500 text-cyan-600" 
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <Microscope className="w-4 h-4" />
          Microbiology Services
        </button>
        <button
          onClick={() => setActiveTab("schedules")}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors whitespace-nowrap flex items-center gap-2 ${
            activeTab === "schedules" 
              ? "border-cyan-500 text-cyan-600" 
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <Repeat className="w-4 h-4" />
          Recurring Schedules
          {schedules.length > 0 && (
            <Badge variant="secondary" className="ml-1">{schedules.length}</Badge>
          )}
        </button>
        <button
          onClick={() => setActiveTab("orders")}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors whitespace-nowrap ${
            activeTab === "orders" 
              ? "border-cyan-500 text-cyan-600" 
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          My Orders ({orders.length})
        </button>
        <button
          onClick={() => setActiveTab("info")}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors whitespace-nowrap ${
            activeTab === "info" 
              ? "border-cyan-500 text-cyan-600" 
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          Lab Info
        </button>
      </div>

      {/* Microbiology Services Tab */}
      {activeTab === "microbiology" && (
        <div className="space-y-8">
          {/* Legionella Featured Section */}
          <div className="bg-gradient-to-r from-red-600 to-rose-600 rounded-2xl p-6 md:p-8 text-white">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-5 h-5 text-yellow-300" />
                  <span className="text-red-200 text-sm font-semibold uppercase tracking-wider">Featured Service</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold mb-2">Same-Day Legionella PCR Testing</h2>
                <p className="text-red-100 text-lg mb-4">
                  ISO certified rapid testing with industry-leading turnaround time
                </p>
                
                <div className="grid sm:grid-cols-3 gap-4 mt-6">
                  <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                    <Timer className="w-8 h-8 text-yellow-300 mb-2" />
                    <p className="font-bold text-xl">In by 9am</p>
                    <p className="text-red-200 text-sm">Sample drop-off deadline</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                    <Zap className="w-8 h-8 text-yellow-300 mb-2" />
                    <p className="font-bold text-xl">Results by 5pm</p>
                    <p className="text-red-200 text-sm">Same business day</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                    <Shield className="w-8 h-8 text-yellow-300 mb-2" />
                    <p className="font-bold text-xl">CDC ELITE</p>
                    <p className="text-red-200 text-sm">Certified laboratory</p>
                  </div>
                </div>
              </div>
              
              <div className="flex-shrink-0">
                <div className="bg-white rounded-2xl p-6 text-slate-900 shadow-xl">
                  <p className="text-red-600 font-semibold mb-1">Starting at</p>
                  <p className="text-4xl font-bold">$150</p>
                  <p className="text-slate-500 text-sm mb-4">per sample</p>
                  <Button 
                    onClick={() => setShowContactDialog(true)}
                    className="w-full bg-red-600 hover:bg-red-700 text-white"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Schedule Test
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Microbiology Test Categories */}
          <div className="grid md:grid-cols-2 gap-6">
            {MICROBIOLOGY_CATEGORIES.map((category) => {
              const Icon = category.icon;
              return (
                <Card key={category.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                  <div className={`bg-gradient-to-r ${category.color} p-4 text-white`}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-lg flex items-center justify-center">
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="font-bold">{category.title}</h3>
                        <p className="text-sm opacity-80">{category.subtitle}</p>
                      </div>
                    </div>
                  </div>
                  <CardContent className="p-0">
                    <div className="divide-y divide-slate-100">
                      {category.tests.map((test, idx) => {
                        // Create a unique test ID for microbiology services
                        const testId = `micro-${category.id}-${idx}`;
                        const testWithId = { ...test, id: testId };
                        const inCart = cart.some(item => item.id === testId);
                        
                        return (
                          <div key={idx} className={`p-4 hover:bg-slate-50 transition-colors ${inCart ? 'bg-emerald-50 ring-1 ring-emerald-200' : ''}`}>
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <p className="font-medium text-slate-900">{test.name}</p>
                                <p className="text-sm text-slate-500">{test.description}</p>
                                <div className="flex items-center gap-2 mt-2">
                                  <Badge variant="outline" className="text-xs">
                                    <Clock className="w-3 h-3 mr-1" />
                                    {test.turnaround}
                                  </Badge>
                                  {inCart && (
                                    <Badge className="bg-emerald-500 text-white text-xs">
                                      <ShoppingCart className="w-3 h-3 mr-1" />
                                      In Cart
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              <div className="text-right flex flex-col items-end gap-2">
                                <div>
                                  <p className="font-bold text-emerald-600">${test.price}</p>
                                  <p className="text-xs text-slate-400">per sample</p>
                                </div>
                                {inCart ? (
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    className="text-red-600 border-red-200 hover:bg-red-50"
                                    onClick={() => removeFromCart(testId)}
                                    data-testid={`remove-micro-${testId}`}
                                  >
                                    <X className="w-3 h-3 mr-1" />
                                    Remove
                                  </Button>
                                ) : (
                                  <Button 
                                    size="sm" 
                                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                    onClick={() => addToCart(testWithId)}
                                    data-testid={`add-micro-${testId}`}
                                  >
                                    <Plus className="w-3 h-3 mr-1" />
                                    Add to Cart
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Full Test List Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TestTube className="w-5 h-5 text-cyan-500" />
                Complete Microbiology Test Menu
              </CardTitle>
              <CardDescription>
                Full list of available microbiology tests with methods and turnaround times
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left p-3 font-semibold text-slate-700">Test Name</th>
                      <th className="text-left p-3 font-semibold text-slate-700">Category</th>
                      <th className="text-left p-3 font-semibold text-slate-700">Method</th>
                      <th className="text-left p-3 font-semibold text-slate-700">TAT</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    <tr className="hover:bg-slate-50"><td className="p-3">Drinking Water Coliform</td><td className="p-3"><Badge variant="outline">Drinking Water</Badge></td><td className="p-3 text-slate-500">Enzyme Substrate</td><td className="p-3">24-48 hrs</td></tr>
                    <tr className="hover:bg-slate-50"><td className="p-3">Total Coliform by Membrane Filtration</td><td className="p-3"><Badge variant="outline">Drinking Water</Badge></td><td className="p-3 text-slate-500">Membrane Filtration</td><td className="p-3">24-48 hrs</td></tr>
                    <tr className="hover:bg-slate-50"><td className="p-3">E.coli by Enzyme Substrate</td><td className="p-3"><Badge variant="outline">Drinking Water</Badge></td><td className="p-3 text-slate-500">Enzyme Substrate</td><td className="p-3">24-48 hrs</td></tr>
                    <tr className="hover:bg-slate-50"><td className="p-3">Legionella PCR (Same-Day)</td><td className="p-3"><Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Legionella</Badge></td><td className="p-3 text-slate-500">PCR</td><td className="p-3 text-red-600 font-medium">Same Day</td></tr>
                    <tr className="hover:bg-slate-50"><td className="p-3">Legionella Pneumophila Serogroup 1</td><td className="p-3"><Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Legionella</Badge></td><td className="p-3 text-slate-500">CDC Culture</td><td className="p-3">3-5 days</td></tr>
                    <tr className="hover:bg-slate-50"><td className="p-3">Legionella Pneumophila Serogroup 2-14</td><td className="p-3"><Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Legionella</Badge></td><td className="p-3 text-slate-500">CDC Culture</td><td className="p-3">3-5 days</td></tr>
                    <tr className="hover:bg-slate-50"><td className="p-3">Heterotrophic Plate Count</td><td className="p-3"><Badge variant="outline">Drinking Water</Badge></td><td className="p-3 text-slate-500">Pour Plate</td><td className="p-3">48-72 hrs</td></tr>
                    <tr className="hover:bg-slate-50"><td className="p-3">Pseudomonas Aeruginosa</td><td className="p-3"><Badge variant="outline">Industrial</Badge></td><td className="p-3 text-slate-500">Membrane Filtration</td><td className="p-3">48-72 hrs</td></tr>
                    <tr className="hover:bg-slate-50"><td className="p-3">Yeast & Mold</td><td className="p-3"><Badge variant="outline">Environmental</Badge></td><td className="p-3 text-slate-500">Culture</td><td className="p-3">5-7 days</td></tr>
                    <tr className="hover:bg-slate-50"><td className="p-3">Staphylococcus Aureus</td><td className="p-3"><Badge variant="outline">Environmental</Badge></td><td className="p-3 text-slate-500">Culture</td><td className="p-3">24-48 hrs</td></tr>
                    <tr className="hover:bg-slate-50"><td className="p-3">MIC Panel (Corrosion)</td><td className="p-3"><Badge variant="outline">Industrial</Badge></td><td className="p-3 text-slate-500">Multiple Methods</td><td className="p-3">7-10 days</td></tr>
                    <tr className="hover:bg-slate-50"><td className="p-3">MSQPCR Mold Testing</td><td className="p-3"><Badge variant="outline">Environmental</Badge></td><td className="p-3 text-slate-500">qPCR</td><td className="p-3">3-5 days</td></tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Contact Sterile Mate */}
          <Card className="bg-gradient-to-br from-teal-600 to-cyan-700 text-white">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
                    <img src={STERILEMATE_LOGO} alt="Sterile Mate" className="h-10 object-contain" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">Schedule Your Testing</h3>
                    <p className="text-cyan-100">Contact Sterile Mate to arrange sample collection and testing</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  <a
                    href="tel:2814563458"
                    className="inline-flex items-center gap-2 px-5 py-3 bg-white text-teal-700 rounded-lg hover:bg-cyan-50 transition-colors font-medium"
                  >
                    <Phone className="w-5 h-5" />
                    (281) 456-3458
                  </a>
                  <a
                    href="mailto:george.broughton@sterilemate.com"
                    className="inline-flex items-center gap-2 px-5 py-3 bg-white/20 text-white rounded-lg hover:bg-white/30 transition-colors font-medium"
                  >
                    <Mail className="w-5 h-5" />
                    Email Us
                  </a>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-white/20 text-sm text-cyan-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <p>Sterile Mate LLC • george.broughton@sterilemate.com</p>
                <a
                  href="https://www.google.com/maps/search/?api=1&query=10100+East+Freeway+Houston+TX+77029"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-white hover:text-cyan-200 transition-colors"
                  data-testid="testing-site-location"
                >
                  <MapPin className="w-4 h-4" />
                  Testing Site: 10100 East Freeway, Houston, TX 77029
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Catalog Tab */}
      {activeTab === "catalog" && (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Test List */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-lg">Available Tests</h2>
              <p className="text-sm text-slate-500">5 Day Turnaround</p>
            </div>
            
            <div className="grid sm:grid-cols-2 gap-3">
              {catalog.tests.map((test) => {
                const Icon = categoryIcons[test.category] || TestTube;
                const inCart = cart.some(item => item.id === test.id);
                
                return (
                  <Card 
                    key={test.id} 
                    className={`transition-all hover:shadow-md ${inCart ? 'ring-2 ring-blue-500 bg-blue-50' : ''}`}
                    data-testid={`test-card-${test.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${categoryColors[test.category] || 'bg-slate-100'}`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-sm">{test.name}</h3>
                            <p className="text-xs text-slate-500 mt-0.5">{test.method}</p>
                            <p className="text-xs text-slate-400 mt-1">{test.description}</p>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="font-bold text-blue-600">${test.price.toFixed(2)}</p>
                          <p className="text-xs text-slate-400">per sample</p>
                        </div>
                      </div>
                      <div className="mt-3 flex justify-end">
                        {inCart ? (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => removeFromCart(test.id)}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Remove
                          </Button>
                        ) : (
                          <Button 
                            size="sm"
                            onClick={() => addToCart(test)}
                            className="bg-blue-600 hover:bg-blue-700"
                            data-testid={`add-test-${test.id}`}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Add to Order
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Cart Summary */}
          <div className="lg:col-span-1">
            <Card className="sticky top-4">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5" />
                  Order Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {cart.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <TestTube className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No tests selected</p>
                    <p className="text-sm">Add tests from the catalog</p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      {cart.map((item) => (
                        <div key={item.id} className="flex items-center justify-between text-sm p-2 bg-slate-50 rounded">
                          <span className="truncate flex-1">{item.name}</span>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">${(item.price * sampleCount).toFixed(2)}</span>
                            <button 
                              onClick={() => removeFromCart(item.id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="border-t pt-3 space-y-2 text-sm">
                      <div className="flex justify-between text-slate-500">
                        <span>Subtotal ({sampleCount} sample{sampleCount !== 1 ? 's' : ''})</span>
                        <span>${subtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-slate-500">
                        <span>Environmental Fee</span>
                        <span>${envFee.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-slate-500">
                        <span>Shipping</span>
                        <span>${shipping.toFixed(2)}</span>
                      </div>
                      {total === catalog.minimum_invoice && subtotal + envFee + shipping < catalog.minimum_invoice && (
                        <div className="flex justify-between text-amber-600 text-xs">
                          <span>Minimum invoice applied</span>
                        </div>
                      )}
                      <div className="flex justify-between font-bold text-lg pt-2 border-t">
                        <span>Total</span>
                        <span className="text-blue-600">${total.toFixed(2)}</span>
                      </div>
                    </div>

                    <Button 
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      onClick={() => setShowOrderDialog(true)}
                      data-testid="proceed-to-order-btn"
                    >
                      Proceed to Order
                    </Button>
                  </>
                )}

                <div className="text-xs text-slate-400 text-center">
                  Minimum invoice: ${catalog.minimum_invoice?.toFixed(2)}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Schedules Tab */}
      {activeTab === "schedules" && (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-slate-900">Recurring Sample Schedules</h2>
              <p className="text-slate-500">Set up automated recurring testing for your sample points</p>
            </div>
            <Button 
              onClick={() => setShowScheduleBuilder(true)}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="create-schedule-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Schedule
            </Button>
          </div>

          {/* Active Schedules */}
          {schedules.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <Repeat className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <h3 className="font-medium text-slate-600">No recurring schedules yet</h3>
                <p className="text-sm text-slate-400 mt-1 mb-4">
                  Create a schedule to automate your sample collection and testing
                </p>
                <Button 
                  variant="outline" 
                  onClick={() => setShowScheduleBuilder(true)}
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Create Your First Schedule
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {schedules.map((schedule) => (
                <Card key={schedule.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="p-3 bg-cyan-100 rounded-lg">
                          <Repeat className="w-6 h-6 text-cyan-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-slate-900">
                              {schedule.frequency.charAt(0).toUpperCase() + schedule.frequency.slice(1)} Testing
                            </h3>
                            <Badge className={schedule.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>
                              {schedule.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-500">
                            {schedule.sample_points?.length} sample points • {schedule.tests?.length} tests
                          </p>
                          <p className="text-xs text-slate-400 mt-1">
                            Next: {schedule.next_date ? new Date(schedule.next_date).toLocaleDateString() : 'Pending'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm">
                          <FileText className="w-4 h-4 mr-1" />
                          View Details
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50">
                          Pause
                        </Button>
                      </div>
                    </div>
                    
                    {/* Sample Points Preview */}
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Sample Points</p>
                      <div className="flex flex-wrap gap-2">
                        {schedule.sample_points?.slice(0, 5).map((sp, idx) => (
                          <Badge key={idx} variant="outline" className="bg-slate-50">
                            {sp.name || sp}
                          </Badge>
                        ))}
                        {schedule.sample_points?.length > 5 && (
                          <Badge variant="outline" className="bg-slate-100">
                            +{schedule.sample_points.length - 5} more
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Schedule Builder Dialog */}
          <Dialog open={showScheduleBuilder} onOpenChange={setShowScheduleBuilder}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-cyan-500" />
                  Create Recurring Sample Schedule
                </DialogTitle>
                <DialogDescription>
                  Set up automated recurring testing for your facility's sample points
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6 py-4">
                {/* Frequency Selection */}
                <div>
                  <Label className="text-sm font-medium">Testing Frequency *</Label>
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    {["weekly", "biweekly", "monthly", "quarterly"].map((freq) => (
                      <button
                        key={freq}
                        onClick={() => setScheduleFrequency(freq)}
                        className={`p-3 rounded-lg border-2 text-center transition-colors ${
                          scheduleFrequency === freq 
                            ? "border-cyan-500 bg-cyan-50 text-cyan-700" 
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <p className="font-medium capitalize">{freq}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Start Date */}
                <div>
                  <Label className="text-sm font-medium">Start Date *</Label>
                  <Input
                    type="date"
                    value={scheduleStartDate}
                    onChange={(e) => setScheduleStartDate(e.target.value)}
                    className="mt-1"
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>

                {/* Sample Points */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm font-medium">Sample Collection Points *</Label>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={addScheduledSamplePoint}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Point
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {scheduledSamplePoints.map((point, index) => (
                      <div key={index} className="flex gap-2 p-3 bg-slate-50 rounded-lg">
                        <div className="flex-1 grid grid-cols-3 gap-2">
                          <Input
                            placeholder="Point Name (e.g., RO-1)"
                            value={point.name}
                            onChange={(e) => updateScheduledSamplePoint(index, "name", e.target.value)}
                          />
                          <Input
                            placeholder="Location (e.g., Building A)"
                            value={point.location}
                            onChange={(e) => updateScheduledSamplePoint(index, "location", e.target.value)}
                          />
                          <Input
                            placeholder="Description (optional)"
                            value={point.description}
                            onChange={(e) => updateScheduledSamplePoint(index, "description", e.target.value)}
                          />
                        </div>
                        {scheduledSamplePoints.length > 1 && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => removeScheduledSamplePoint(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Test Selection */}
                <div>
                  <Label className="text-sm font-medium">Select Tests *</Label>
                  <p className="text-xs text-slate-500 mb-3">
                    Choose which tests to perform at each sample point
                  </p>
                  <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-1">
                    {MICROBIOLOGY_CATEGORIES.flatMap(cat => cat.tests).map((test, idx) => {
                      const isSelected = selectedScheduleTests.some(t => t.name === test.name);
                      return (
                        <button
                          key={idx}
                          onClick={() => toggleScheduleTest({ id: `test-${idx}`, ...test })}
                          className={`p-3 rounded-lg border text-left transition-colors ${
                            isSelected 
                              ? "border-cyan-500 bg-cyan-50" 
                              : "border-slate-200 hover:border-slate-300"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-sm">{test.name}</p>
                              <p className="text-xs text-slate-500">{test.turnaround}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-emerald-600 font-medium text-sm">${test.price}</span>
                              {isSelected && <Check className="w-4 h-4 text-cyan-500" />}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  {selectedScheduleTests.length > 0 && (
                    <p className="text-sm text-cyan-600 mt-2">
                      {selectedScheduleTests.length} test(s) selected • Est. ${selectedScheduleTests.reduce((sum, t) => sum + t.price, 0)} per sample point
                    </p>
                  )}
                </div>

                {/* Summary */}
                {scheduledSamplePoints.filter(sp => sp.name).length > 0 && selectedScheduleTests.length > 0 && (
                  <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-4">
                    <h4 className="font-semibold text-cyan-900 mb-2">Schedule Summary</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-cyan-600">Frequency</p>
                        <p className="font-medium capitalize">{scheduleFrequency}</p>
                      </div>
                      <div>
                        <p className="text-cyan-600">Sample Points</p>
                        <p className="font-medium">{scheduledSamplePoints.filter(sp => sp.name).length}</p>
                      </div>
                      <div>
                        <p className="text-cyan-600">Tests per Point</p>
                        <p className="font-medium">{selectedScheduleTests.length}</p>
                      </div>
                      <div>
                        <p className="text-cyan-600">Est. Cost per Cycle</p>
                        <p className="font-medium text-emerald-600">
                          ${(scheduledSamplePoints.filter(sp => sp.name).length * selectedScheduleTests.reduce((sum, t) => sum + t.price, 0)).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => { setShowScheduleBuilder(false); resetScheduleForm(); }}>
                  Cancel
                </Button>
                <Button 
                  onClick={saveRecurringSchedule}
                  disabled={savingSchedule || !scheduleStartDate || scheduledSamplePoints.filter(sp => sp.name).length === 0 || selectedScheduleTests.length === 0}
                  className="bg-cyan-600 hover:bg-cyan-700"
                >
                  {savingSchedule ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CalendarDays className="w-4 h-4 mr-2" />
                  )}
                  Create Schedule
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* Orders Tab */}
      {activeTab === "orders" && (
        <div className="space-y-4">
          {orders.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Package className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <h3 className="font-medium text-slate-600">No orders yet</h3>
                <p className="text-sm text-slate-400 mt-1">Your water testing orders will appear here</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setActiveTab("catalog")}
                >
                  Browse Test Catalog
                </Button>
              </CardContent>
            </Card>
          ) : (
            orders.map((order) => (
              <Card key={order.id} data-testid={`order-${order.id}`} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-lg ${
                        order.status === 'completed' ? 'bg-green-100' :
                        order.status === 'in_progress' ? 'bg-blue-100' :
                        order.status === 'cancelled' ? 'bg-red-100' :
                        'bg-amber-100'
                      }`}>
                        {order.status === 'completed' ? <CheckCircle2 className="w-6 h-6 text-green-600" /> :
                         order.status === 'in_progress' ? <Loader2 className="w-6 h-6 text-blue-600 animate-spin" /> :
                         order.status === 'cancelled' ? <AlertCircle className="w-6 h-6 text-red-600" /> :
                         <Clock className="w-6 h-6 text-amber-600" />}
                      </div>
                      <div>
                        <h3 className="font-semibold">{order.id}</h3>
                        <p className="text-sm text-slate-500">{order.location_name}</p>
                        <p className="text-xs text-slate-400 mt-1">
                          {order.tests?.length} tests • {order.sample_points?.length} sample points
                        </p>
                        <p className="text-xs text-slate-400">
                          Created: {new Date(order.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <Badge className={
                        order.status === 'completed' ? 'bg-green-100 text-green-700' :
                        order.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                        order.status === 'results_ready' ? 'bg-purple-100 text-purple-700' :
                        order.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                        'bg-amber-100 text-amber-700'
                      }>
                        {order.status?.replace(/_/g, ' ').toUpperCase()}
                      </Badge>
                      <p className="text-lg font-bold text-blue-600">
                        ${order.total_hospital_price?.toFixed(2)}
                      </p>
                      <p className="text-xs text-slate-400">
                        Assigned to: {order.assigned_contractor_name}
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2"
                        onClick={() => {
                          setSelectedOrder(order);
                          setShowOrderDetailDialog(true);
                        }}
                        data-testid={`view-order-${order.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View Details
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Lab Info Tab */}
      {activeTab === "info" && (
        <div className="grid md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-500" />
                AB Labs
              </CardTitle>
              <CardDescription>Your Environmental Testing Partner</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="font-medium">Address</p>
                  <p className="text-sm text-slate-500">5445 Almeda Road, Houston, TX 77004</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="font-medium">Phone</p>
                  <p className="text-sm text-slate-500">(713) 863-9790</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="font-medium">Email</p>
                  <p className="text-sm text-slate-500">sterilemate@ablabs.com</p>
                </div>
              </div>
              <div className="pt-4 border-t">
                <p className="font-medium mb-2">Certifications</p>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline">NELAP</Badge>
                  <Badge variant="outline">TNI</Badge>
                  <Badge variant="outline">A2LA</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-500" />
                Resources
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <a 
                href="https://sterilemate.com/bottle-request-form/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 transition-colors"
              >
                <span className="font-medium">Bottle Request Form</span>
                <ExternalLink className="w-4 h-4 text-slate-400" />
              </a>
              <a 
                href="https://sterilemate.com/wp-content/uploads/2025/11/environmental-coc.xls" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 transition-colors"
              >
                <span className="font-medium">Chain of Custody (COC) Download</span>
                <ExternalLink className="w-4 h-4 text-slate-400" />
              </a>
              <a 
                href="https://sterilemate.com/accreditations/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 transition-colors"
              >
                <span className="font-medium">View Accreditations</span>
                <ExternalLink className="w-4 h-4 text-slate-400" />
              </a>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Contact Sterile Mate Dialog */}
      <Dialog open={showContactDialog} onOpenChange={setShowContactDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-teal-500" />
              Schedule Your Test
            </DialogTitle>
            <DialogDescription>
              Contact Sterile Mate to arrange your microbiology testing
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Logo */}
            <div className="flex justify-center">
              <div className="bg-slate-900 rounded-xl px-6 py-3">
                <img src={STERILEMATE_LOGO} alt="Sterile Mate" className="h-10 object-contain" />
              </div>
            </div>

            {/* Contact Options */}
            <div className="space-y-3">
              <a
                href="tel:2814563458"
                className="flex items-center gap-4 p-4 bg-teal-50 border border-teal-200 rounded-xl hover:bg-teal-100 transition-colors"
              >
                <div className="w-12 h-12 bg-teal-500 rounded-xl flex items-center justify-center">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-teal-900">Call Us</p>
                  <p className="text-teal-700">(281) 456-3458</p>
                </div>
              </a>

              <a
                href="mailto:george.broughton@sterilemate.com?subject=Microbiology Testing Inquiry"
                className="flex items-center gap-4 p-4 bg-cyan-50 border border-cyan-200 rounded-xl hover:bg-cyan-100 transition-colors"
              >
                <div className="w-12 h-12 bg-cyan-500 rounded-xl flex items-center justify-center">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-cyan-900">Email Us</p>
                  <p className="text-cyan-700">george.broughton@sterilemate.com</p>
                </div>
              </a>
            </div>

            {/* Address - Testing Site */}
            <a 
              href="https://www.google.com/maps/search/?api=1&query=10100+East+Freeway+Houston+TX+77029"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg text-sm hover:bg-slate-100 transition-colors cursor-pointer"
            >
              <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
              <div>
                <p className="font-medium text-slate-700">Testing Site</p>
                <p className="text-slate-500">10100 East Freeway</p>
                <p className="text-slate-500">Houston, TX 77029</p>
              </div>
            </a>

            {/* Note */}
            <div className="text-center text-sm text-slate-500">
              <p>Our team will help you schedule sample collection and select the right tests for your facility.</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContactDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order Dialog */}
      <Dialog open={showOrderDialog} onOpenChange={setShowOrderDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-blue-500" />
              Complete Your Order
            </DialogTitle>
            <DialogDescription>
              Provide sample collection details for your water testing order
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Location */}
            <div>
              <Label className="text-sm font-medium">Location Name *</Label>
              <Input
                placeholder="e.g., Building A - Water Treatment Room"
                value={locationName}
                onChange={(e) => setLocationName(e.target.value)}
                className="mt-1"
                data-testid="location-name-input"
              />
            </div>

            {/* Sample Points */}
            <div>
              <Label className="text-sm font-medium">Sample Collection Points *</Label>
              <p className="text-xs text-slate-500 mb-2">
                Each test will be performed on each sample point
              </p>
              <div className="space-y-2">
                {samplePoints.map((point, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      placeholder={`Sample point ${index + 1} (e.g., RO Unit 1, DI Loop 3)`}
                      value={point}
                      onChange={(e) => updateSamplePoint(index, e.target.value)}
                      data-testid={`sample-point-${index}`}
                    />
                    {samplePoints.length > 1 && (
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => removeSamplePoint(index)}
                        className="text-red-500"
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={addSamplePoint}
                  className="mt-2"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Sample Point
                </Button>
              </div>
            </div>

            {/* Selected Tests */}
            <div>
              <Label className="text-sm font-medium">Selected Tests ({cart.length})</Label>
              <div className="mt-2 p-3 bg-slate-50 rounded-lg space-y-1">
                {cart.map((test) => (
                  <div key={test.id} className="flex justify-between text-sm">
                    <span>{test.name}</span>
                    <span className="font-medium">${(test.price * sampleCount).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label className="text-sm font-medium">Additional Notes</Label>
              <Textarea
                placeholder="Any special instructions or requirements..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="mt-1"
                rows={3}
              />
            </div>

            {/* Purchase Order Section */}
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <ClipboardList className="w-5 h-5 text-amber-600" />
                <h4 className="font-medium text-amber-900">Purchase Order (Optional)</h4>
              </div>
              
              {/* PO Number */}
              <div>
                <Label className="text-sm font-medium text-amber-800">PO Number</Label>
                <Input
                  placeholder="Enter your Purchase Order number"
                  value={poNumber}
                  onChange={(e) => setPoNumber(e.target.value)}
                  className="mt-1 border-amber-300 focus:border-amber-500"
                  data-testid="po-number-input"
                />
              </div>

              {/* PO File Upload */}
              <div>
                <Label className="text-sm font-medium text-amber-800">Upload PO Document</Label>
                <div className="mt-1">
                  {poFileName ? (
                    <div className="flex items-center justify-between p-3 bg-white border border-amber-300 rounded-lg">
                      <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-amber-600" />
                        <span className="text-sm font-medium text-slate-700">{poFileName}</span>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={removePoFile}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-amber-300 rounded-lg cursor-pointer hover:border-amber-400 hover:bg-amber-50/50 transition-colors">
                      <Upload className="w-6 h-6 text-amber-500 mb-2" />
                      <span className="text-sm text-amber-700 font-medium">Click to upload PO</span>
                      <span className="text-xs text-amber-500">PDF, DOC, or image (max 10MB)</span>
                      <input
                        type="file"
                        className="hidden"
                        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                        onChange={handlePoFileUpload}
                        data-testid="po-file-input"
                      />
                    </label>
                  )}
                </div>
              </div>
            </div>

            {/* Price Summary */}
            <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg">
              <div className="flex justify-between text-sm mb-2">
                <span>Tests ({cart.length} × {sampleCount} samples)</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm mb-2">
                <span>Environmental Fee</span>
                <span>${envFee.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm mb-2">
                <span>Shipping</span>
                <span>${shipping.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-bold text-lg pt-2 border-t border-blue-200">
                <span>Total</span>
                <span className="text-blue-600">${total.toFixed(2)}</span>
              </div>
              <p className="text-xs text-blue-600 mt-2">
                <Clock className="w-3 h-3 inline mr-1" />
                Estimated completion: 5-7 business days
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowOrderDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitOrder}
              disabled={submitting || !locationName.trim() || samplePoints.filter(sp => sp.trim()).length === 0}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="submit-order-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Submit Order
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order Detail Dialog */}
      <Dialog open={showOrderDetailDialog} onOpenChange={setShowOrderDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-blue-500" />
              Order Details - {selectedOrder?.id}
            </DialogTitle>
            <DialogDescription>
              View complete order information and history
            </DialogDescription>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-6">
              {/* Order Status Banner */}
              <div className={`p-4 rounded-lg ${
                selectedOrder.status === 'completed' ? 'bg-green-50 border border-green-200' :
                selectedOrder.status === 'in_progress' ? 'bg-blue-50 border border-blue-200' :
                selectedOrder.status === 'cancelled' ? 'bg-red-50 border border-red-200' :
                'bg-amber-50 border border-amber-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {selectedOrder.status === 'completed' ? <CheckCircle2 className="w-6 h-6 text-green-600" /> :
                     selectedOrder.status === 'in_progress' ? <Loader2 className="w-6 h-6 text-blue-600" /> :
                     selectedOrder.status === 'cancelled' ? <AlertCircle className="w-6 h-6 text-red-600" /> :
                     <Clock className="w-6 h-6 text-amber-600" />}
                    <div>
                      <p className="font-semibold text-slate-800">
                        Status: {selectedOrder.status?.replace(/_/g, ' ').toUpperCase()}
                      </p>
                      <p className="text-sm text-slate-500">
                        Last updated: {new Date(selectedOrder.updated_at || selectedOrder.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <Badge className={
                    selectedOrder.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                    selectedOrder.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                    'bg-slate-100 text-slate-700'
                  }>
                    {selectedOrder.priority?.toUpperCase() || 'STANDARD'}
                  </Badge>
                </div>
              </div>
              
              {/* Order Info Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Location</p>
                  <p className="font-medium">{selectedOrder.location_name}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Created</p>
                  <p className="font-medium">{new Date(selectedOrder.created_at).toLocaleDateString()}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Requested By</p>
                  <p className="font-medium">{selectedOrder.requested_by || 'N/A'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Assigned To</p>
                  <p className="font-medium">{selectedOrder.assigned_contractor_name || 'Pending Assignment'}</p>
                </div>
                {selectedOrder.po_number && (
                  <div className="space-y-1">
                    <p className="text-sm text-slate-500">PO Number</p>
                    <p className="font-medium">{selectedOrder.po_number}</p>
                  </div>
                )}
              </div>
              
              {/* Sample Points */}
              <div>
                <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-slate-500" />
                  Sample Collection Points ({selectedOrder.sample_points?.length || 0})
                </h4>
                <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                  {selectedOrder.sample_points?.map((point, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-medium">
                        {idx + 1}
                      </span>
                      <span>{typeof point === 'string' ? point : point.name || point.location}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Tests Ordered */}
              <div>
                <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <TestTube className="w-4 h-4 text-slate-500" />
                  Tests Ordered ({selectedOrder.tests?.length || 0})
                </h4>
                <div className="space-y-2">
                  {selectedOrder.tests?.map((test, idx) => (
                    <div key={idx} className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium">{test.name}</p>
                        <p className="text-xs text-slate-500">{test.turnaround || '5 Days'} turnaround</p>
                      </div>
                      <p className="font-semibold text-blue-600">${(test.hospital_price || test.price || 0).toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Pricing Summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-3">Order Total</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Subtotal</span>
                    <span>${(selectedOrder.subtotal || selectedOrder.total_hospital_price || 0).toFixed(2)}</span>
                  </div>
                  {selectedOrder.fees?.map((fee, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span className="text-slate-600">{fee.name}</span>
                      <span>${fee.amount?.toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="border-t border-blue-200 pt-2 flex justify-between font-bold">
                    <span>Total</span>
                    <span className="text-blue-600">${(selectedOrder.total_hospital_price || 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>
              
              {/* Notes */}
              {selectedOrder.notes && (
                <div>
                  <h4 className="font-semibold text-slate-800 mb-2">Notes</h4>
                  <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">{selectedOrder.notes}</p>
                </div>
              )}
              
              {/* Results Section (if completed) */}
              {selectedOrder.status === 'completed' && selectedOrder.results && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5" />
                    Test Results Available
                  </h4>
                  <p className="text-sm text-green-700">Results have been uploaded and are ready for review.</p>
                  <Button size="sm" className="mt-3 bg-green-600 hover:bg-green-700">
                    <Download className="w-4 h-4 mr-1" />
                    Download Results
                  </Button>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowOrderDetailDialog(false)}>
              Close
            </Button>
            {selectedOrder?.status === 'pending' && (
              <Button
                variant="destructive"
                onClick={async () => {
                  try {
                    const res = await fetch(`${API}/api/environmental-testing/orders/${selectedOrder.id}/status`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ status: 'cancelled' })
                    });
                    if (res.ok) {
                      toast.success('Order cancelled');
                      fetchOrders();
                      setShowOrderDetailDialog(false);
                    }
                  } catch (err) {
                    toast.error('Failed to cancel order');
                  }
                }}
              >
                Cancel Order
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
